<template>
  <div>
    <page-header
      left-side-class="col-xl-8"
      right-side-class="col-xl-4 d-flex align-items-end justify-content-end pb-2"
      :page="headerPage"
      :actions="headerActions"
    />
    <section-title
      has-line
      :content="title"
      container-class="tag-badge-info-custom"
    />
    <validation-observer tag="form" ref="observer">
      <div class="bg-gray-9 px-4 py-3 rounded">
        <book-info
          v-model="model"
          :grades="collections.grades"
          :centers="collections.centers"
          @center="val => getMunicipalities(val?.id)"
        />
        <properties
          class="mt-2"
          v-model="model"
          :publishers="collections.publishers"
          :authors="collections.authors"
          :reading-mediums="collections.readingMediums"
        />
        <location-info
          class="mt-2"
          v-model="model"
          :centers="collections.centers"
          :municipalities="collections.municipalities"
          :schools="collections.schools"
          :locationTypes="collections.locationTypes"
          @center="val => getMunicipalities(val?.id)"
          @municipality="val => getSchools(val?.id)"
        />
      </div>
    </validation-observer>
  </div>
</template>

<script>
import PageHeader from "PageHeader";
import SectionTitle from "SectionTitle";
import ActionButton from "ActionButton";

import BookInfo from "./components/BookInfo";
import Properties from "./components/Properties";
import LocationInfo from "./components/LocationInfo";

import alertMixin from "@/mixin/alert.mixin";

import inventoryCatalogService from "@/services/api/inventorycatalog.api";
import locationService from "@/services/api/location.api";
import academicService from "@/services/api/academicUtils.api.js";
import * as bookService from "@/modules/books-requests/api";

import EduEspCode from "@/utils/constants/eduEspCode";
import EduEspTextCodes from "@/utils/constants/EduEspTextCodes";

import LocationTypes from "@/utils/constants/inventory/inventoryOrderBookLocationTypes";
import PROPERTIES from "@/utils/constants/inventory/bookItemProperties";

export default {
  name: "BookLoadEdit",
  components: {
    ActionButton,
    PageHeader,
    SectionTitle,
    BookInfo,
    Properties,
    LocationInfo
  },
  mixins: [alertMixin],
  data() {
    return {
      model: {
        name: null,
        center: null,
        municipality: null,
        school: null,
        grade: null,
        location: null,
        description: null,
        publisher: null,
        author: null,
        readingMedium: null,
        isbn: null,
        locationType: null
      },
      collections: {
        grades: [],
        centers: [],
        municipalities: [],
        schools: [],
        publishers: [],
        authors: [],
        readingMediums: [],
        locationTypes: [
          LocationTypes.CSEE,
          LocationTypes.CENTER,
          LocationTypes.SCHOOL
        ]
      },
      properties: [],
      book: {}
    };
  },
  computed: {
    title() {
      return this.isEditing ? "Editar Libro" : "Cargar Libro";
    },
    isEditing() {
      return Boolean(this.bookId);
    },
    catalogId() {
      return Number(this.$route.params.id);
    },
    bookId() {
      return Number(this.$route.params.bookId);
    },
    academicYearId() {
      return this.currentAcademicYear().id;
    },
    headerPage() {
      return {
        icon: "fas fa-book-open",
        title: this.title,
        routes: ["Inicio", "Libros", "Catálogo de Libros", this.title]
      };
    },
    headerActions() {
      return [
        {
          text: "Regresar",
          icon: "fas fa-arrow-circle-left",
          variant: "outline-success",
          click: () => this.$router.go(-1)
        },
        {
          text: "Guardar",
          icon: "fas fa-save",
          variant: "success",
          click: () => this.save()
        }
      ];
    },
    isLocationSchool() {
      return this.model?.locationType?.id === LocationTypes.SCHOOL.id;
    }
  },
  async mounted() {
    await this.initData();
  },
  methods: {
    async initData() {
      await this.getBook();
      await Promise.allSettled([
        this.getGrades(),
        this.getCenters(),
        this.getPublishers(),
        this.getAuthors(),
        this.getReadingMediums()
      ]);
      await this.setModel();
    },
    async setModel() {
      if (!this.book) {
        this.model = {};
        return;
      }
      this.model.name = this.book.title ?? this.book.name;
      if (!this.isEditing) return;
      this.model.id = this.book.id;
      this.model.grade = this.book.gradeId
        ? { id: this.book.gradeId, name: this.book.grade }
        : null;
      this.model.center = this.book.caatId
        ? { id: this.book.caatId, name: this.book.caat }
        : null;
      this.model.description = this.book.description;
      if (this.book.caatId) await this.getMunicipalities(this.book.caatId);
      this.model.municipality = this.book.municipalityId
        ? {
            id: this.book.municipalityId,
            name: this.book.municipality
          }
        : null;
      if (this.book.municipalityId)
        await this.getSchools(this.book.municipalityId);
      this.model.school = this.book.schoolId
        ? { id: this.book.schoolId, name: this.book.school }
        : null;
      this.model.location = this.book.address;

      this.model.locationType = this.book.locationTypeId
        ? this.collections.locationTypes.find(
            l => l.id == this.book.locationTypeId
          )
        : null;

      const { properties } = this.book;
      const { publishers, authors, readingMediums } = this.collections;

      const publisher = properties.find(x => x.name === PROPERTIES.Publisher);
      this.model.publisher = publishers.find(x => x.name === publisher?.value);

      const author = properties.find(x => x.name === PROPERTIES.Author);
      this.model.author = authors.find(x => x.name === author?.value);

      const rm = properties.find(x => x.name === PROPERTIES.ReadingMedium);
      this.model.readingMedium = readingMediums.find(x => x.name === rm?.value);

      const isbn = properties.find(x => x.name === PROPERTIES.Isbn);
      this.model.isbn = isbn?.value;
    },
    async getBook() {
      const { data } = this.isEditing
        ? await inventoryCatalogService.getBook(this.bookId)
        : await inventoryCatalogService.get(this.catalogId);
      this.book = data;
    },
    async getGrades() {
      const { data } = await academicService.getGrades();
      this.collections.grades = data;
    },
    async getCenters() {
      const { data } = await locationService.getCenters({
        academicYearId: this.academicYearId
      });
      this.collections.centers = data;
    },
    async getMunicipalities(centerId = null) {
      this.model.municipality = null;
      this.collections.municipalities = [];
      this.model.school = null;
      this.collections.schools = [];

      if (!centerId) return;
      if (!this.isLocationSchool) return;
      const sameCenter =
        this.collections.municipalities.length &&
        this.collections.municipalities.every(s => s.centerId === centerId);

      if (sameCenter) return;

      const { data } = await locationService.getMunicipalities({
        academicYearId: this.academicYearId,
        centerId
      });
      this.collections.municipalities = centerId
        ? data.map(x => ({ ...x, centerId }))
        : data;
    },
    async getSchools(municipalityId = null) {
      this.model.school = null;
      this.collections.schools = [];

      if (!municipalityId) return;

      const sameMunicipality =
        this.collections.schools.length &&
        this.collections.schools.every(
          s => s.municipalityId === municipalityId
        );
      if (sameMunicipality) return;

      const { data } = await locationService.getSchools({
        academicYearId: this.academicYearId,
        municipalityId
      });
      this.collections.schools = municipalityId
        ? data.map(x => ({ ...x, municipalityId }))
        : data;
    },
    async getPublishers() {
      const { data } = await bookService.getBooksPublishers();
      this.collections.publishers = data.filter(
        e => e.name.toUpperCase() !== EduEspTextCodes.NotAvailable
      );
    },
    async getAuthors() {
      const { data } = await bookService.getBooksAuthors();
      this.collections.authors = data.filter(
        e => e.name.toUpperCase() !== EduEspTextCodes.NotAvailable
      );
    },
    async getReadingMediums() {
      const { data } = await academicService.getEduEsp({
        codetype: EduEspCode.CatlibReadingmedium
      });
      this.collections.readingMediums = data;
    },
    async save() {
      const valid = await this.$refs.observer.validate();
      if (!valid) return;

      const {
        center,
        grade,
        publisher,
        author,
        readingMedium,
        isbn
      } = this.model;

      const locationTypeId = this.model?.locationType?.id;
      const hasCenter = locationTypeId > LocationTypes.CSEE.id;
      const payload = {
        name: this.model.name,
        gradeId: grade?.id,
        centerId: hasCenter ? center?.id : null,
        address: this.model.location,
        description: this.model.description,
        catalogId: this.catalogId,
        schoolId: hasCenter ? this.model?.school?.id : null,
        locationTypeId,
        properties: [
          { name: PROPERTIES.Publisher, detailName: publisher?.name },
          { name: PROPERTIES.Author, detailName: author?.name },
          { name: PROPERTIES.ReadingMedium, detailName: readingMedium?.name },
          { name: PROPERTIES.Isbn, detailName: isbn }
        ]
      };

      if (!this.isEditing) payload.quantity = Number(this.model.quantity);

      const isConfirmed = await this.ShowCancelModal(
        "Atención",
        `¿Está seguro que desea guardar la información documentada al momento?`,
        "Si",
        "No"
      );
      if (isConfirmed) {
        try {
          this.isEditing
            ? await inventoryCatalogService.editBook(this.bookId, payload)
            : await inventoryCatalogService.load(payload);
          this.$router.go(-1);
        } catch (error) {
          this.ShowToast("Error", error?.response?.data?.message, "error");
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped></style>
